
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { LockKeyhole } from 'lucide-react';
import { useAccessControl } from '@/hooks/useAccessControl';

const AccessControl: React.FC = () => {
  const [accessCode, setAccessCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const { verifyAccessCode, isLoggedIn, isAdmin, clientId } = useAccessControl();
  const navigate = useNavigate();
  const [webflowClientId, setWebflowClientId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  
  useEffect(() => {
    const storedClientId = localStorage.getItem('webflow_versioning_client_id');
    console.log('[AccessControl] Found webflow client ID:', storedClientId);
    setWebflowClientId(storedClientId);
    setIsInitializing(false);
  }, []);

  // Handle redirect after login
  useEffect(() => {
    if (isInitializing) return;
    if (!isLoggedIn) return;

    console.log('[AccessControl] Logged in, preparing redirect:', { 
      isAdmin, 
      clientId,
      webflowClientId 
    });
    
    // Check if we have a webflow client ID to return to
    if (webflowClientId) {
      console.log('[AccessControl] Redirecting to client page:', webflowClientId);
      navigate(`/clients/${webflowClientId}`);
    } else if (isAdmin) {
      // Admin users go to admin panel
      console.log('[AccessControl] Admin user, redirecting to admin panel');
      navigate('/admin');
    } else if (clientId) {
      // Client users go to their client page
      console.log('[AccessControl] Client user, redirecting to client page:', clientId);
      navigate(`/clients/${clientId}`);
    } else {
      // Fallback to home
      console.log('[AccessControl] No specific destination, redirecting to home');
      navigate('/');
    }
  }, [isLoggedIn, isAdmin, clientId, webflowClientId, navigate, isInitializing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessCode.trim()) return;
    
    setIsVerifying(true);
    try {
      console.log('[AccessControl] Verifying access code...');
      const success = await verifyAccessCode(accessCode);
      console.log('[AccessControl] Access code verification result:', success);
      // The useEffect above will handle redirects based on login state and webflow client ID
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="bg-primary/10 p-3 rounded-full">
              <LockKeyhole className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-center">Access Required</CardTitle>
          <CardDescription className="text-center">
            Enter your access code to view or manage releases
            {webflowClientId && (
              <div className="mt-2 text-sm text-blue-600">
                (Client ID: {webflowClientId})
              </div>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="text"
              placeholder="Enter access code"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              className="text-center"
              autoFocus
              autoComplete="off"
            />
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isVerifying || !accessCode.trim()}
            >
              {isVerifying ? 'Verifying...' : 'Continue'}
            </Button>
            <p className="text-xs text-center text-gray-500 mt-4">
              Contact your administrator if you don't have an access code
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccessControl;
